import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Tabs, TabsContent } from '../../../../../components/ui/tabs';
import { ResultsGrid, type ResultsGridEditMode } from '../ResultsGrid';
import { MessagesPanel } from '../MessagesPanel';
import { ExecutionPlanPanelHost } from '../../../execution-plan/components/ExecutionPlanPanelLazy';
import { ChartPanel } from '../../../charts/components/ChartPanel';
import { SpatialResultsMapPanel } from '../../../spatial/results-map/SpatialResultsMapPanel';
import { useSpatialResultColumns } from '../../../spatial/results-map/spatialResultColumns';
import { PinnedResultsTabContent } from './PinnedResultsTabContent';
import { SkippedRowsPanel } from '../../../query-editor/components/SkippedRowsPanel';
import { ResultsPanelTabBar } from './ResultsPanelTabBar';
import { currentRunMessages } from './useResultsPanelState';
import type { ResultsBearingContent, ResultsPanelState } from './useResultsPanelState';
import type { DatabaseEngineId } from '../../../../../core/engines';
import type { ResultDisplayMode, QueryMessage } from '../../../core/types/workspaceTypes';
import type { ChartConfig } from '../../../charts/types/chartTypes';
import type { QueryColumn } from '../../../../../types/queryExecution';
import type { ResultColumnTarget } from '../../utils/resultColumnWhereField';
import type { AddColumnToFilter } from '../../utils/addColumnToFilter';
import { useAnalytics } from '../../../../../core/analytics';

// Stable empty fallbacks for ResultsGrid — fresh `[]` literals in the JSX
// would give the props a new identity every render and defeat its React.memo.
const EMPTY_RESULTS: unknown[] = [];
const EMPTY_RESULT_COLUMNS: { id?: string; name: string }[] = [];

type ResultsPanelProps = {
  state: ResultsPanelState;
  tabId: string;
  content: ResultsBearingContent | undefined;
  connectionId?: string;
  database?: string;
  engineId: DatabaseEngineId;
  isExecuting: boolean;
  isCancelling: boolean;
  isLongRunning: boolean;
  onCancel: (opts: { source: 'toolbar' | 'long_running_indicator' | 'population' }) => void;
  /** T13 — "AI: Explain this error" on an error row in the Messages panel. */
  onExplainError?: (message: QueryMessage) => void;
  /**
   * One-click next step on a classified error's tip row (`queryErrorAction.ts`).
   * Only the Query Editor wires this — rows with an `action` stay inert without it.
   */
  onMessageAction?: (message: QueryMessage) => void;
  /** Same `isAiLauncherDisabled` gate the rest of the feature uses. */
  aiCliAvailable?: boolean;
  aiCliDetectionLoading?: boolean;
  onReExecute?: () => void;
  displayMode: ResultDisplayMode;
  /** QE-only: results editing. VQE omits it. */
  editMode?: ResultsGridEditMode;
  /** QE-only: quick WHERE builder. VQE omits it. */
  onAddToWhere?: (columnName: string, value: unknown, column: ResultColumnTarget) => void;
  /** QE-only: a column header's "Add to Filter" (the Visual Query Editor's condition editor). */
  addColumnToFilter?: AddColumnToFilter;
  /** Total row count of the active result set — drives the chart's sample note. */
  totalRowCount?: number;
  /** VQE-only (T4) stale-results notice; QE passes nothing. */
  staleNotice?: string;
  /** Shortcut hint for the collapse toggle title (QE passes 'Ctrl+J'). */
  collapseShortcutHint?: string;
  /** Height of the tab bar row (source default 28px). */
  tabsBarHeight?: number;
  /** S20 — pin to dashboard. The HOST supplies SQL/connection provenance; the
   * pane adds what each inner surface knows (result shape, chart config). */
  onPinToDashboard?: (extras: {
    from: 'qe-results' | 'qe-chart' | 'plan';
    chartConfig?: ChartConfig;
    shape?: { columns: QueryColumn[]; rows: Record<string, unknown>[] };
  }) => void;
};

/**
 * The Query Editor's bottom results pane, extracted as a dumb component so both
 * the Query Editor and the Visual Query Editor can mount the SAME results
 * experience (grid + copy/export toolbar + Results/Messages/Plan/Chart/Pinned
 * tabs). All pane-scoped state comes in via `state` (`useResultsPanelState`),
 * called by the host so its existing tab-switch writers keep working.
 */
export function ResultsPanel({
  state,
  tabId,
  content,
  connectionId,
  database,
  engineId,
  isExecuting,
  isCancelling,
  isLongRunning,
  onCancel,
  onExplainError,
  onMessageAction,
  aiCliAvailable,
  aiCliDetectionLoading,
  onReExecute,
  displayMode,
  editMode,
  onAddToWhere,
  addColumnToFilter,
  totalRowCount,
  staleNotice,
  collapseShortcutHint,
  tabsBarHeight = 28,
  onPinToDashboard,
}: ResultsPanelProps) {
  const { track } = useAnalytics();

  // Spatial Map view (T13): the Map trigger renders only when the active result set carries ≥1
  // native-spatial column. `mapSelectedRowIndex` bridges feature↔row selection between the map panel
  // and the results grid's controlled-selection seam.
  const spatialResultColumns = useSpatialResultColumns(
    state.activeResultSet,
    engineId,
    connectionId,
    database,
  );
  const hasSpatialColumns = spatialResultColumns.spatialColumnIds.length > 0;

  // Task 27 (A19/G18) — Chart tab exclusion set, derived from the same categorization
  // above rather than re-sniffed. Excludes native-spatial + declared WKT/GeoJSON ids,
  // but NOT declared lat/lng-PAIR ids: those key on the LAT column for the map's "one
  // logical cell" semantics, while the underlying lat/lng NUMERIC columns are legitimate
  // chart axes and must stay chartable.
  const chartSpatialColumnIds = useMemo(() => {
    const ids = new Set<string>();
    for (const id of spatialResultColumns.spatialColumnIds) {
      const decl = spatialResultColumns.declaredSpatialColumns.get(id);
      if (decl?.format === 'lat-lng-pair') continue;
      ids.add(id);
    }
    return ids;
  }, [spatialResultColumns]);
  const [gridSelectedRowIndex, setGridSelectedRowIndex] = useState<number | null>(null);
  // Clear the cross-surface selection whenever the active result set changes (new query / tab).
  useEffect(() => {
    setGridSelectedRowIndex(null);
  }, [state.activeResultSet]);
  // If the Map tab is open when the result set loses its spatial column, fall back to Results
  // (the Map trigger unmounts and Radix would otherwise render an empty pane).
  const { setActiveTab } = state;
  useEffect(() => {
    if (!hasSpatialColumns) setActiveTab((prev) => (prev === 'map' ? 'results' : prev));
  }, [hasSpatialColumns, setActiveTab]);

  // Chunk-deferral gate for the code-split Plan panel. The Plan sub-tab is
  // `forceMount`ed so its local view state survives sub-tab switches, which
  // would otherwise fetch the execution-plan chunk on every mount. Mount it the
  // moment a plan artifact exists (so switching to Plan is still instant for the
  // run that produced it) or the user selects the sub-tab, and keep it mounted
  // from then on — identical semantics to the old always-on mount, minus the
  // boot cost for users who never open a plan.
  const [planPanelMounted, setPlanPanelMounted] = useState(false);
  const planPanelNeeded = state.activeTab === 'plan' || Boolean(content?.executionPlan);
  useEffect(() => {
    if (planPanelNeeded) setPlanPanelMounted(true);
  }, [planPanelNeeded]);

  // CSV-export skipped rows (S2 follow-up): 0-based absolute indexes of the rows the last
  // "Export All to CSV" could not read, reported by useWindowedGrid via ResultsGrid. Non-null
  // ⇒ the conditional "Skipped rows (N)" bottom tab renders (same pattern as the Map tab).
  const [exportSkippedRows, setExportSkippedRows] = useState<number[] | null>(null);
  // Bumped with every skip report: keys SkippedRowsPanel so a SECOND export of
  // the same (still open) result set remounts it — the panel's one-shot
  // sequential preview fetch would otherwise never run for the new list and its
  // rows would sit at "…" forever.
  const [exportSkippedRowsSeq, setExportSkippedRowsSeq] = useState(0);
  const hasSkippedRows = exportSkippedRows != null && exportSkippedRows.length > 0;
  // If the Skipped-rows tab is open when its state clears, fall back to Results.
  useEffect(() => {
    if (!hasSkippedRows) setActiveTab((prev) => (prev === 'skipped-rows' ? 'results' : prev));
  }, [hasSkippedRows, setActiveTab]);
  // The skipped-row indexes point into the exported result set's buffer — they die with it.
  // Clear on re-run (cacheGeneration bump) and on result-set switch (activeCacheUri change):
  // exactly when the buffered set they index goes away.
  useEffect(() => {
    setExportSkippedRows(null);
  }, [state.activeCacheUri, content?.cacheGeneration]);
  // Stable Stop handlers — inline arrows here would defeat the memoization of
  // ResultsGrid / MessagesPanel on every pane render. If the long-running
  // threshold has already fired, the user is reacting to the "taking longer"
  // affordance, not a populating-rows cancel.
  const handleCancelPopulation = useCallback(
    () => onCancel({ source: isLongRunning ? 'long_running_indicator' : 'population' }),
    [onCancel, isLongRunning],
  );
  const handleStopFromMessages = useCallback(
    () => onCancel({ source: isLongRunning ? 'long_running_indicator' : 'toolbar' }),
    [onCancel, isLongRunning],
  );

  const handleExportSkippedRows = useCallback((rows: number[]) => {
    setExportSkippedRows(rows);
    setExportSkippedRowsSeq((seq) => seq + 1);
  }, []);

  // T13 — spatial Map view enter/leave analytics (both directions, `enabled` distinguishes).
  const { activeTab, handleTabChange } = state;
  const handleTabChangeWithMapAnalytics = useCallback(
    (next: string) => {
      if (next !== activeTab) {
        if (next === 'map') {
          track('feature:spatial_map_view', { surface: 'query', engine: engineId, enabled: true });
        } else if (activeTab === 'map') {
          track('feature:spatial_map_view', { surface: 'query', engine: engineId, enabled: false });
        }
      }
      handleTabChange(next);
    },
    [activeTab, handleTabChange, track, engineId],
  );

  // Both handlers below navigate on the user's behalf, so they go through the
  // analytics-wrapped tab change (map enter/leave pairing) — via a ref so their
  // own identities stay stable for ResultsGrid's memo.
  const handleTabChangeRef = useRef(handleTabChangeWithMapAnalytics);
  handleTabChangeRef.current = handleTabChangeWithMapAnalytics;
  // "Review skipped rows" on the export toast → land on the durable tab.
  const handleReviewSkippedRows = useCallback(() => {
    handleTabChangeRef.current('skipped-rows');
  }, []);
  // Skipped-rows panel row click → activate Results and drive the grid's controlled
  // selection (the SAME externalSelectedRowIndex channel the Map tab uses). The channel
  // only reacts to a CHANGE, so a re-click of the already-selected row passes through
  // null for one commit and re-selects on the next frame — the user asked to see the
  // row again and may have scrolled away since.
  const gridSelectedRowIndexRef = useRef(gridSelectedRowIndex);
  gridSelectedRowIndexRef.current = gridSelectedRowIndex;
  const activeCacheUriRef = useRef(state.activeCacheUri);
  activeCacheUriRef.current = state.activeCacheUri;
  const cacheGenerationRef = useRef(content?.cacheGeneration);
  cacheGenerationRef.current = content?.cacheGeneration;
  const handleSkippedRowJump = useCallback((rowIndex0: number) => {
    handleTabChangeRef.current('results');
    if (gridSelectedRowIndexRef.current === rowIndex0) {
      setGridSelectedRowIndex(null);
      // The re-select frame is not tied to the result-set lifecycle — if the
      // query re-runs or the active result set switches before it fires, the
      // old index would select an unrelated row in the NEW set. Re-check both.
      const uriAtClick = activeCacheUriRef.current;
      const genAtClick = cacheGenerationRef.current;
      requestAnimationFrame(() => {
        if (activeCacheUriRef.current !== uriAtClick || cacheGenerationRef.current !== genAtClick) return;
        setGridSelectedRowIndex(rowIndex0);
      });
      return;
    }
    setGridSelectedRowIndex(rowIndex0);
  }, []);

  return (
    <Tabs
      value={state.activeTab}
      onValueChange={handleTabChangeWithMapAnalytics}
      className="flex-1 min-h-0 flex flex-col"
    >
      {/* Content area - takes remaining space */}
      <div
        className="flex-1 min-h-0 overflow-hidden"
        data-testid="results-pane-content"
        style={state.collapsed ? { height: 0, flex: 'none' } : undefined}
      >
        <TabsContent
          forceMount
          value="results"
          className="h-full m-0 p-0 data-[state=inactive]:hidden"
        >
          <ResultsGrid
            tabId={tabId}
            resultSets={state.effectiveResultSets}
            // The CURRENT run's messages: the Output view and the strip's
            // Output tab describe one execution, while `content.messages` is
            // the accumulated log the Messages tab below renders in full.
            messages={currentRunMessages(content)}
            results={content?.results || EMPTY_RESULTS}
            columns={content?.columns || EMPTY_RESULT_COLUMNS}
            isLoading={isExecuting}
            displayMode={displayMode}
            editMode={editMode}
            zoomLevel={state.zoomLevel}
            connectionId={connectionId}
            database={database}
            engineId={engineId}
            cacheUri={state.activeCacheUri}
            resultSetCacheUris={content?.resultSetCacheUris}
            remoteTotalRowCount={state.activeRowCount}
            cacheGeneration={content?.cacheGeneration}
            capabilities={state.activeCapabilities}
            onAddToWhere={onAddToWhere}
            addColumnToFilter={addColumnToFilter}
            onPin={state.handlePin}
            onPinToDashboard={
              onPinToDashboard
                ? (shape) => onPinToDashboard({ from: 'qe-results', shape })
                : undefined
            }
            onActiveResultSetChange={state.handleActiveResultSetChange}
            onScrollPositionChange={state.handleScrollPositionChange}
            onSourceGenerationChange={state.handleSourceGenerationChange}
            onReExecute={onReExecute}
            externalSelectedRowIndex={hasSpatialColumns || hasSkippedRows ? gridSelectedRowIndex : undefined}
            onSelectedRowIndexChange={hasSpatialColumns || hasSkippedRows ? setGridSelectedRowIndex : undefined}
            onExportSkippedRows={handleExportSkippedRows}
            onReviewSkippedRows={handleReviewSkippedRows}
            isPopulating={state.populationEntry?.state === 'populating'}
            onCancelPopulation={handleCancelPopulation}
            isCancelling={isCancelling}
            isLongRunning={isLongRunning}
          />
        </TabsContent>

        <TabsContent
          forceMount
          value="messages"
          className="h-full m-0 p-0 data-[state=inactive]:hidden"
        >
          <MessagesPanel
            messages={content?.messages}
            className="h-full"
            isLongRunning={isLongRunning}
            isCancelling={isCancelling}
            onStop={handleStopFromMessages}
            onExplainError={onExplainError}
            onMessageAction={onMessageAction}
            aiCliAvailable={aiCliAvailable}
            aiCliDetectionLoading={aiCliDetectionLoading}
          />
        </TabsContent>

        <TabsContent
          forceMount
          value="plan"
          className="h-full m-0 p-0 data-[state=inactive]:hidden"
        >
          {planPanelMounted && (
            <ExecutionPlanPanelHost
              artifact={content?.executionPlan}
              className="h-full"
              onPinToDashboard={
                onPinToDashboard && content?.executionPlan
                  ? () => onPinToDashboard({ from: 'plan' })
                  : undefined
              }
            />
          )}
        </TabsContent>

        <TabsContent
          forceMount
          value="chart"
          className="h-full m-0 p-0 data-[state=inactive]:hidden"
        >
          <ChartPanel
            resultSet={state.chartResultSet}
            chartConfig={state.activeChartConfig}
            onConfigChange={state.handleChartConfigChange}
            spatialColumnIds={chartSpatialColumnIds}
            onPinToDashboard={
              onPinToDashboard
                ? (chartConfig) =>
                    onPinToDashboard({
                      from: 'qe-chart',
                      chartConfig,
                      shape: state.chartResultSet
                        ? {
                            columns: state.chartResultSet.columns,
                            rows: state.chartResultSet.rows.slice(0, 50),
                          }
                        : undefined,
                    })
                : undefined
            }
            infoNote={[
              state.chartFromRenderHint ? 'Chart from | render — full data in the Results tab.' : null,
              state.isWindowed && state.chartBufferRows.length > 0
                ? `Chart built from first ${state.chartBufferRows.length.toLocaleString()} of ${(totalRowCount ?? 0).toLocaleString()} rows.`
                : null,
            ].filter(Boolean).join(' ') || undefined}
          />
        </TabsContent>

        {hasSpatialColumns && (
          <TabsContent value="map" className="h-full m-0 p-0">
            <SpatialResultsMapPanel
              resultSet={state.activeResultSet}
              engine={engineId}
              connectionId={connectionId}
              database={database}
              selectedRowIndex={gridSelectedRowIndex}
              onRowSelect={setGridSelectedRowIndex}
              windowed={
                state.isWindowed && state.activeCacheUri
                  ? {
                      cacheUri: state.activeCacheUri,
                      generation: state.sourceGeneration,
                      totalRowCount: state.activeRowCount ?? 0,
                    }
                  : undefined
              }
            />
          </TabsContent>
        )}

        {hasSkippedRows && (
          <TabsContent
            // forceMount so the lazily fetched previews survive tab switches —
            // SkippedRowsPanel latches its one-time sequential fetch on `active`.
            forceMount
            value="skipped-rows"
            className="h-full m-0 p-0 data-[state=inactive]:hidden"
          >
            <SkippedRowsPanel
              // Remount per skip report — a repeat export of the same result set
              // gets fresh previews (see exportSkippedRowsSeq).
              key={exportSkippedRowsSeq}
              skippedRows={exportSkippedRows!}
              cacheUri={state.activeCacheUri}
              columns={state.activeResultSet?.columns ?? EMPTY_RESULT_COLUMNS}
              active={state.activeTab === 'skipped-rows'}
              sourceGeneration={state.sourceGeneration}
              onRowJump={handleSkippedRowJump}
            />
          </TabsContent>
        )}

        {content?.pinnedResultSets?.map((pinned) => (
          <TabsContent
            key={pinned.id}
            forceMount
            value={`pinned-${pinned.id}`}
            className="h-full m-0 p-0 data-[state=inactive]:hidden"
          >
            <PinnedResultsTabContent
              pinned={pinned}
              tabId={tabId}
              zoomLevel={state.zoomLevel}
              connectionId={connectionId}
              database={database}
              engineId={engineId}
            />
          </TabsContent>
        ))}
      </div>

      {/* Tab bar - fixed at bottom of tabs container */}
      <ResultsPanelTabBar
        state={state}
        content={content}
        tabsBarHeight={tabsBarHeight}
        collapseShortcutHint={collapseShortcutHint}
        staleNotice={staleNotice}
        hasSpatialColumns={hasSpatialColumns}
        skippedRows={exportSkippedRows}
      />
    </Tabs>
  );
}
